import type { Role, Rarity } from '@stanwars/sim';
import dailyThemesData from '../../../../content/daily-themes.json';

export interface LocalizedName { legal: string; parody: string; }

export interface BundledGroup {
  id: string;
  name: LocalizedName;
  debutYear: number;
  colorPrimary: string;
  synergy: { name: string; effect: string; amount: number };
}

export interface BundledMember {
  id: string;
  groupId: string;
  stageName: LocalizedName;
  role: Role;
  rarity: Rarity;
  stats: { hp: number; atk: number; def: number; spd: number; crit: number };
}

export interface BundledSkill {
  id: string;
  ownerMemberId: string;
  type: 'passive' | 'active' | 'ultimate';
  archetypeId: string;
  params: Record<string, number | string | boolean>;
  cooldown: number;
  unlockLevel: number;
  flavorName: string;
  flavorText: string;
  sourceReference?: string;
}

export interface BundledStage {
  id: string;
  title: string;
  intro: string;
  enemyLineup: Array<{
    memberId: string;
    level: number;
    loadout: { passiveId: string; activeIds: string[]; ultimateId: string };
  }>;
  rewards: { gold: number; tickets: number; shards: Array<{ memberId: string; count: number } | string> };
  boss: boolean;
  modifier: string | null;
}

// ─── Vite eager JSON imports ───────────────────────────────────────────────────
const groupModules = import.meta.glob('../../../../content/groups/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, BundledGroup>;

const memberModules = import.meta.glob('../../../../content/members/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, BundledMember>;

const skillModules = import.meta.glob('../../../../content/skills/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, BundledSkill[]>;

const stageModules = import.meta.glob('../../../../content/stages/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, BundledStage[]>;

const dailyThemes = dailyThemesData as Record<string, unknown>;

// ─── Build indexes ─────────────────────────────────────────────────────────────
const groups = new Map<string, BundledGroup>();
for (const m of Object.values(groupModules)) {
  groups.set(m.id, m);
}

const members = new Map<string, BundledMember>();
for (const m of Object.values(memberModules)) {
  members.set(m.id, m);
}

const skillsById = new Map<string, BundledSkill>();
const skillsByOwner = new Map<string, BundledSkill[]>();
for (const arr of Object.values(skillModules)) {
  for (const s of arr) {
    skillsById.set(s.id, s);
    const list = skillsByOwner.get(s.ownerMemberId) ?? [];
    list.push(s);
    skillsByOwner.set(s.ownerMemberId, list);
  }
}

const stagesById = new Map<string, BundledStage>();
const stagesByChapter = new Map<number, BundledStage[]>();
for (const arr of Object.values(stageModules)) {
  for (const s of arr) {
    stagesById.set(s.id, s);
    const chapter = parseInt(s.id.split('-')[0]!, 10);
    const list = stagesByChapter.get(chapter) ?? [];
    list.push(s);
    stagesByChapter.set(chapter, list);
  }
}

// ─── Public content API ───────────────────────────────────────────────────────
export const content = {
  groups,
  members,
  skillsById,
  skillsByOwner,
  stagesById,
  stagesByChapter,
  dailyThemes,

  getMember: (id: string): BundledMember | undefined => members.get(id),
  getGroup: (id: string): BundledGroup | undefined => groups.get(id),
  getSkill: (id: string): BundledSkill | undefined => skillsById.get(id),
  getSkillsForMember: (memberId: string): BundledSkill[] => skillsByOwner.get(memberId) ?? [],

  getSkillsByType: (type: BundledSkill['type']): BundledSkill[] => {
    const result: BundledSkill[] = [];
    for (const s of skillsById.values()) {
      if (s.type === type) result.push(s);
    }
    return result;
  },

  getStage: (id: string): BundledStage | undefined => stagesById.get(id),

  getChapter: (n: number): BundledStage[] =>
    (stagesByChapter.get(n) ?? []).sort((a, b) => {
      const an = parseInt(a.id.split('-')[1]!, 10);
      const bn = parseInt(b.id.split('-')[1]!, 10);
      return an - bn;
    }),

  chapters: (): number[] => [...stagesByChapter.keys()].sort((a, b) => a - b),
};

// ─── Display locale helpers ────────────────────────────────────────────────────
export type DisplayLocale = 'legal' | 'parody';

export function displayName(member: BundledMember, locale: DisplayLocale): string {
  return member.stageName[locale];
}

export function groupDisplayName(group: BundledGroup, locale: DisplayLocale): string {
  return group.name[locale];
}

/** Reads VITE_STANWARS_LEGAL_MODE and returns the active display locale. */
export function useDisplayLocale(): DisplayLocale {
  const mode = import.meta.env.VITE_STANWARS_LEGAL_MODE as string | undefined;
  return mode === 'parody' ? 'parody' : 'legal';
}
